<script setup lang="ts">
import { type Component, ref } from "vue";
import TreatmentStartsWidget from "@/components/shared/analytics/TreatmentStartsWidget.vue";
import ActivePatientNetworkWidget from "@/components/shared/analytics/ActivePatientNetworkWidget.vue";
import TotalPracticesWidget from "@/components/shared/analytics/TotalPracticesWidget.vue";
import StartToPatientRatioWidget from "@/components/shared/analytics/StartToPatientRatioWidget.vue";
import PatientReadinessLevelsWidget from "@/components/shared/analytics/PatientReadinessLevelsWidget.vue";
import ClockWidget from "@/components/shared/analytics/ClockWidget.vue";
import PatientConversionFunnelWidget from "@/components/shared/analytics/PatientConversionFunnelWidget.vue";
import PatientRetentionRateWidget from "@/components/shared/analytics/PatientRetentionRateWidget.vue";
import TreatmentStartsTrendWidget from "@/components/shared/analytics/TreatmentStartsTrendWidget.vue";
import PatientProgressionDistributionWidget from "@/components/shared/analytics/PatientProgressionDistributionWidget.vue";
import DoctorEngagementWidget from "@/components/shared/analytics/DoctorEngagementWidget.vue";
import DataQualityAlertsWidget from "@/components/shared/analytics/DataQualityAlertsWidget.vue";
import BaseResizableWidget from "@/components/shared/analytics/baseWidgets/BaseResizableWidget.vue";
import { GridLayout, GridItem } from "grid-layout-plus";

definePage({
  meta: {
    title: "generic.navbar.paths.analytics",
    checkPrivileges: () => {
      return true;
    },
  },
});

enum EDashboardTile {
  TREATMENT_STARTS = "TREATMENT_STARTS",
  ACTIVE_PATIENT_NETWORK = "ACTIVE_PATIENT_NETWORK",
  TOTAL_PRACTICES = "TOTAL_PRACTICES",
  START_TO_PATIENT_RATIO = "START_TO_PATIENT_RATIO",
  PATIENT_READINESS_LEVEL = "PATIENT_READINESS_LEVEL",
  CLOCK = "CLOCK",
  PATIENT_CONVERSION_FUNNEL = "PATIENT_CONVERSION_FUNNEL",
  PATIENT_RETENTION_RATE = "PATIENT_RETENTION_RATE",
  TREATMENT_STARTS_TREND = "TREATMENT_STARTS_TREND",
  PATIENT_PROGRESSION_DISTRIBUTION = "PATIENT_PROGRESSION_DISTRIBUTION",
  DOCTOR_ENGAGEMENT = "DOCTOR_ENGAGEMENT",
  DATA_QUALITY_ALERTS = "DATA_QUALITY_ALERTS",
}

const layout = ref([
  { x: 0, y: 0, w: 3, h: 4, i: "0", component: EDashboardTile.CLOCK },
  { x: 3, y: 0, w: 3, h: 4, i: "1", component: EDashboardTile.TREATMENT_STARTS },
  { x: 6, y: 0, w: 3, h: 4, i: "2", component: EDashboardTile.ACTIVE_PATIENT_NETWORK },
  { x: 9, y: 0, w: 3, h: 4, i: "3", component: EDashboardTile.TOTAL_PRACTICES },
  { x: 12, y: 0, w: 3, h: 4, i: "4", component: EDashboardTile.START_TO_PATIENT_RATIO },
  { x: 0, y: 9, w: 10, h: 10, i: "5", component: EDashboardTile.TREATMENT_STARTS_TREND },
  { x: 10, y: 4, w: 5, h: 10, i: "6", component: EDashboardTile.PATIENT_PROGRESSION_DISTRIBUTION },
  { x: 0, y: 14, w: 6, h: 8, i: "7", component: EDashboardTile.PATIENT_CONVERSION_FUNNEL },
  // { x: 6, y: 14, w: 3, h: 8, i: "8", component: EDashboardTile.PATIENT_RETENTION_RATE },
  { x: 6, y: 14, w: 3, h: 8, i: "8", component: EDashboardTile.DOCTOR_ENGAGEMENT },
  // { x: 9, y: 14, w: 6, h: 8, i: "9", component: EDashboardTile.PATIENT_READINESS_LEVEL },
  { x: 9, y: 14, w: 6, h: 8, i: "9", component: EDashboardTile.DATA_QUALITY_ALERTS },
]);

const componentMap: Record<EDashboardTile, Component> = {
  [EDashboardTile.TREATMENT_STARTS]: TreatmentStartsWidget,
  [EDashboardTile.ACTIVE_PATIENT_NETWORK]: ActivePatientNetworkWidget,
  [EDashboardTile.TOTAL_PRACTICES]: TotalPracticesWidget,
  [EDashboardTile.START_TO_PATIENT_RATIO]: StartToPatientRatioWidget,
  [EDashboardTile.PATIENT_READINESS_LEVEL]: PatientReadinessLevelsWidget,
  [EDashboardTile.DATA_QUALITY_ALERTS]: DataQualityAlertsWidget,
  [EDashboardTile.CLOCK]: ClockWidget,
  [EDashboardTile.PATIENT_CONVERSION_FUNNEL]: PatientConversionFunnelWidget,
  [EDashboardTile.PATIENT_RETENTION_RATE]: PatientRetentionRateWidget,
  [EDashboardTile.TREATMENT_STARTS_TREND]: TreatmentStartsTrendWidget,
  [EDashboardTile.PATIENT_PROGRESSION_DISTRIBUTION]: PatientProgressionDistributionWidget,
  [EDashboardTile.DOCTOR_ENGAGEMENT]: DoctorEngagementWidget,
};

const componentDefaultDimensions: Record<EDashboardTile, { w: number; h: number }> = {
  [EDashboardTile.TREATMENT_STARTS]: { w: 3, h: 4 },
  [EDashboardTile.ACTIVE_PATIENT_NETWORK]: { w: 3, h: 4 },
  [EDashboardTile.TOTAL_PRACTICES]: { w: 3, h: 4 },
  [EDashboardTile.START_TO_PATIENT_RATIO]: { w: 3, h: 4 },
  [EDashboardTile.PATIENT_READINESS_LEVEL]: { w: 6, h: 8 },
  [EDashboardTile.DATA_QUALITY_ALERTS]: { w: 6, h: 8 },
  [EDashboardTile.CLOCK]: { w: 3, h: 4 },
  [EDashboardTile.PATIENT_CONVERSION_FUNNEL]: { w: 6, h: 8 },
  [EDashboardTile.PATIENT_RETENTION_RATE]: { w: 3, h: 8 },
  [EDashboardTile.TREATMENT_STARTS_TREND]: { w: 10, h: 10 },
  [EDashboardTile.PATIENT_PROGRESSION_DISTRIBUTION]: { w: 5, h: 10 },
  [EDashboardTile.DOCTOR_ENGAGEMENT]: { w: 3, h: 8 },
};

// @ts-ignore
const gridWrapperRef = ref<HTMLElement>();
// @ts-ignore
const gridLayoutRef = ref<HTMLElement>();
</script>

<template>
  <div>
    <div
      ref="gridWrapperRef"
      class="tw:!h-full tw:!w-full"
    >
      <grid-layout
        ref="gridLayoutRef"
        v-model:layout="layout"
        :col-num="15"
        :row-height="25"
        :is-draggable="true"
        :is-resizable="true"
        use-css-transforms
        vertical-compact
      >
        <grid-item
          v-for="item in layout"
          :key="item.i"
          :x="item.x"
          :y="item.y"
          :w="item.w"
          :h="item.h"
          :i="item.i"
        >
          <base-resizable-widget
            :size="{ w: item.w, h: item.h }"
            :original-size="componentDefaultDimensions[item.component as EDashboardTile]"
          >
            <template #default="{ size }">
              <component
                :is="componentMap[item.component as EDashboardTile]"
                :size="size"
              />
            </template>
          </base-resizable-widget>
        </grid-item>
      </grid-layout>
    </div>
  </div>
</template>

<style scoped>
.vgl-layout {
  --vgl-placeholder-bg: transparent;
}
</style>
